import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle } from 'react-feather';
import { Badge, Button, Card, Col, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getTemplateVersionsAPI, rollbackTemplateAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import ConfirmModal from '../../../Components/shared/ConfirmModal';
import { platformUrl } from '../../../utils/mhNav';
import styles from './TemplateVersions.module.scss';

function diffLines(left, right) {
  const la = (left || '').split('\n');
  const lb = (right || '').split('\n');
  const max = Math.max(la.length, lb.length);
  const rows = [];
  for (let i = 0; i < max; i += 1) {
    const a = la[i];
    const b = lb[i];
    rows.push({
      i,
      left: a,
      right: b,
      same: a === b,
    });
  }
  return rows;
}

export default function TemplateVersions() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);
  const [rollback, setRollback] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getTemplateVersionsAPI(id);
      setVersions(res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSel = (v) => {
    setSelected((prev) => {
      const exists = prev.find((x) => x.id === v.id);
      if (exists) return prev.filter((x) => x.id !== v.id);
      if (prev.length >= 2) return [prev[1], v];
      return [...prev, v];
    });
  };

  const sortedSel = useMemo(() => [...selected].sort((a, b) => a.versionNum - b.versionNum), [selected]);
  const older = sortedSel[0];
  const newer = sortedSel[1];
  const rows = older && newer ? diffLines(older.bodyMarkdown, newer.bodyMarkdown) : [];

  if (loading && !versions.length) {
    return (
      <div>
        <PageHeader
          title="Versions"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Templates', path: platformUrl('templates') },
            { label: id },
            { label: 'Versions' },
          ]}
        />
        <LoadingSkeleton rows={5} cols={2} />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader
          title="Versions"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Templates', path: platformUrl('templates') },
            { label: id },
          ]}
        />
        <EmptyState icon={AlertTriangle} title="Could not load" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={`Template versions — ${id}`}
        breadcrumbs={[
          { label: 'Platform', path: platformUrl('dashboard') },
          { label: 'Templates', path: platformUrl('templates') },
          { label: 'Edit', path: platformUrl(`templates/${id}`) },
          { label: 'Versions' },
        ]}
        secondaryActions={[{ label: 'Back to editor', variant: 'outline-secondary', onClick: () => navigate(platformUrl(`templates/${id}`)) }]}
      />

      <p className="text-muted small">{selected.length < 2 ? 'Select two versions to compare.' : `Comparing ${older.versionLabel} → ${newer.versionLabel}`}</p>

      <Row className="g-3">
        <Col lg={4}>
          <Card>
            <Card.Header>Timeline</Card.Header>
            <Card.Body>
              <ul className={styles.timeline}>
                {versions.map((v) => {
                  const active = selected.some((s) => s.id === v.id);
                  return (
                    <li
                      key={v.id}
                      className={`${styles.timelineItem} ${active ? styles.timelineItemSelected : ''}`}
                      onClick={() => toggleSel(v)}
                    >
                      <div className="fw-semibold">{v.versionLabel}</div>
                      <div className="small text-muted">{v.author}</div>
                      <div className="small">{new Date(v.publishedAt).toLocaleString()}</div>
                      <Badge bg={v.status === 'current' ? 'success' : 'secondary'}>{v.status}</Badge>
                      {v.status !== 'current' && (
                        <Button
                          size="sm"
                          variant="outline-warning"
                          className="mt-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRollback(v);
                          }}
                        >
                          Rollback
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={8}>
          <Card>
            <Card.Header>Diff</Card.Header>
            <Card.Body>
              {selected.length < 2 ? (
                <p className="text-muted mb-0">Choose two versions on the left.</p>
              ) : (
                <Row className="g-2">
                  <Col md={6}>
                    <div className="small text-muted mb-1">{older.versionLabel} (older)</div>
                    <div className={styles.diffCol}>
                      {rows.map((r) => (
                        <div key={r.i} className={!r.same ? styles.diffRemoved : ''}>
                          {r.left ?? ''}
                        </div>
                      ))}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="small text-muted mb-1">{newer.versionLabel} (newer)</div>
                    <div className={styles.diffCol}>
                      {rows.map((r) => (
                        <div key={r.i} className={!r.same ? styles.diffAdded : ''}>
                          {r.right ?? ''}
                        </div>
                      ))}
                    </div>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <ConfirmModal
        isOpen={Boolean(rollback)}
        onClose={() => setRollback(null)}
        onConfirm={async () => {
          try {
            await rollbackTemplateAPI(id, rollback.versionNum);
            toast.success('Rollback queued');
            setRollback(null);
            load();
          } catch (e) {
            toast.error(e.message);
          }
        }}
        title="Rollback template?"
        message="This will republish the selected version as current. Downstream channels may need a few minutes to refresh."
        confirmLabel="Rollback"
        danger
      />
    </div>
  );
}
